import React, { lazy, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { DownloadRounded, EditRounded, Star, StarOutline } from '@mui/icons-material';

import { TYPE_DEFAULT, TYPE_FAVORITES } from '../../constants/channel';
import {
  GET_INVITATIONS,
  INVITATIONS_EXTERNAL_SHARE,
  WEAVO_DOWNLOAD_VIDEO_FROM_WATCH,
  WEAVO_TEAM_SHARE,
} from '../../constants/permission';
import { CACHE_AND_SERVICES } from '../../constants/playlists';
import { videoPropTypes } from '../../constants/propTypes';
import {
  DEFAULT_VIEW_PATH,
  SEARCH_AI_VIEW_PATH,
  SEARCH_VIEW_PATH,
  SHARE_VIEW_PATH,
  SINGLE_VIEW_PATH,
} from '../../constants/routePath';
import { TYPE_ERROR, TYPE_INFO, TYPE_SUCCESS, TYPE_WARNING } from '../../constants/snackbar';
import { ID_DOWNLOAD, ID_EDIT, ID_FAVORITES, ID_SHARE } from '../../constants/socialButtons';
import { MAIN_PLAYER_INSTANCE_KEY } from '../Player/constants';

import { isInternalType } from '../../helpers/embedCodeParams';
import { downloadFile, getFileUrlByDimensions } from '../../helpers/file';
import { hasPermission } from '../../helpers/user';
import * as commonSelectors from '../../redux/selectors/common';
import {
  addFavoritesAction,
  cancelFavoritesUpdatingAction,
  channelsFetchStartAction,
  deleteFavoritesAction,
  getFavoritesAction,
  updateFavoritesChannelAction,
} from '../../redux/slices/common';
import { notifyAction } from '../../redux/slices/snackbar';
import { usePlayerWidget } from '../Player/helpers';
import { canUserDownloadVideo, canUserEditVideo, canUserShareVideo, getEditVideoLink } from './helpers/accessControl';

import DefaultSuspense from '../DefaultSuspense/DefaultSuspense';
import { IconButton, Skeleton, Stack, Tooltip } from '@/mui/components';
import { CustomReplyAlt } from '@/mui/components/CustomIcon';

import styles from './SocialButtons.module.scss';

const InternalShare = lazy(() => import('./Share/InternalShare/InternalShare'));
const ExternalShare = lazy(() => import('./Share/ExternalShare/ExternalShare'));

function SocialButtons({
  video = null,
  channelId = null,
  channelType = TYPE_DEFAULT,
  fromInlineChannel = false,
  ...props
}) {
  const LWP = usePlayerWidget(MAIN_PLAYER_INSTANCE_KEY);
  const dispatch = useDispatch();
  const emailShare = useSelector(commonSelectors.emailShareSelector);
  const socialShare = useSelector(commonSelectors.socialShareSelector);
  const download = useSelector(commonSelectors.downloadSelector);
  const isFavoritesVideoWasLoaded = useSelector(commonSelectors.isFavoritesVideoWasLoadedSelector);
  const isFavoritesUpdating = useSelector(commonSelectors.isFavoritesUpdatingSelector);
  const channels = useSelector(commonSelectors.channelsSelector);
  const favoritesVideo = useSelector(commonSelectors.favoritesVideoSelector);
  const favoritesVideoRef = React.useRef(null);
  favoritesVideoRef.current = favoritesVideo;

  const [showShareDialog, setShowShareDialog] = useState(false);
  const [isVideoDownloading, setIsVideoDownloading] = useState(false);
  const [isFavorites, setIsFavorites] = useState(false);

  const canUseEdit = video && canUserEditVideo(video);

  const canUseDownload =
    video &&
    download &&
    (!isInternalType ||
      (isInternalType && hasPermission(WEAVO_DOWNLOAD_VIDEO_FROM_WATCH) && canUserDownloadVideo(video.access)));

  const canUseShare = video && canUserShareVideo(video.access);
  const canUseInternalShare =
    isInternalType &&
    (hasPermission(WEAVO_TEAM_SHARE) || (hasPermission(INVITATIONS_EXTERNAL_SHARE) && hasPermission(GET_INVITATIONS)));
  const canUseExternalShare = !isInternalType && (socialShare || emailShare);

  const canUseFavorites = isInternalType;

  const unMountChannelCheck = () => {
    if (canUseFavorites) {
      if (channelType === TYPE_FAVORITES) {
        dispatch(updateFavoritesChannelAction());
      } else {
        const favoritesChannel = channels.find((channel) => channel.type === TYPE_FAVORITES);
        if (favoritesChannel) {
          const isAddedFavorites = favoritesVideoRef.current.filter(
            (mediaid) => !favoritesChannel.playlist.find((video$) => video$.mediaid === mediaid),
          );

          if (isAddedFavorites.length) {
            dispatch(
              channelsFetchStartAction({
                cacheUsage: CACHE_AND_SERVICES,
                channels: [favoritesChannel],
              }),
            );
          }

          dispatch(updateFavoritesChannelAction());
        }
      }
      dispatch(cancelFavoritesUpdatingAction());
    }
  };

  useEffect(() => {
    if (canUseFavorites && !isFavoritesVideoWasLoaded) {
      dispatch(getFavoritesAction());
    }

    return unMountChannelCheck;
  }, []);

  useEffect(() => {
    const isFavorites$ = video && favoritesVideo.length && favoritesVideo.some((mediaid) => mediaid === video.mediaid);
    setIsFavorites(isFavorites$);
  }, [favoritesVideo.length, video?.mediaid]);

  useEffect(() => {
    setShowShareDialog(false);
    dispatch(cancelFavoritesUpdatingAction());
  }, [video]);

  const socialButtons = [
    {
      provider: ID_EDIT,
      Icon: EditRounded,
      isVisible: isInternalType && canUseEdit,
      disabled: false,
      tooltip: 'Edit video',
      adOpsDataAIcon: 'social-button-edit-icon',
      onClick: () => window.open(getEditVideoLink(video), '_blank'),
    },
    {
      provider: ID_DOWNLOAD,
      Icon: DownloadRounded,
      isVisible: canUseDownload,
      disabled: isVideoDownloading,
      tooltip: isVideoDownloading ? 'Video is downloading' : 'Download video',
      adOpsDataAIcon: 'social-button-download-icon',
      onClick: () => {
        const videoUrl = getFileUrlByDimensions(video.files);

        if (!videoUrl) {
          dispatch(
            notifyAction({
              variant: TYPE_WARNING,
              message: 'No video to download',
            }),
          );
        } else {
          LWP.pause(video.mediaid);
          dispatch(
            notifyAction({
              variant: TYPE_INFO,
              message: 'Download video started',
            }),
          );
          setIsVideoDownloading(true);

          downloadFile(videoUrl, video.title, (isSuccess) => {
            dispatch(
              notifyAction({
                variant: isSuccess ? TYPE_SUCCESS : TYPE_ERROR,
                message: isSuccess ? 'Video downloaded successfully' : 'Video was not downloaded',
              }),
            );
            setIsVideoDownloading(false);
          });
        }
      },
    },
    {
      provider: ID_SHARE,
      Icon: CustomReplyAlt,
      isVisible: canUseInternalShare || canUseExternalShare,
      disabled: !canUseShare,
      tooltip: canUseShare ? 'Share video' : 'Only the owner can share a private video',
      adOpsDataAIcon: 'social-button-share-icon',
      onClick: () => {
        LWP.pause(video.mediaid);
        setShowShareDialog(true);
      },
    },
    {
      provider: ID_FAVORITES,
      Icon: isFavorites ? Star : StarOutline,
      isVisible: canUseFavorites,
      disabled: isFavoritesUpdating,
      tooltip: isFavorites ? 'Remove from Favorites' : 'Add to Favorites',
      adOpsDataAIcon: isFavorites ? 'social-button-favorites-icon' : 'social-button-favorites-icon-active',
      onClick: () => {
        if (isFavorites) {
          dispatch(deleteFavoritesAction(video.mediaid));
        } else {
          dispatch(addFavoritesAction(video.mediaid));
        }
      },
    },
  ].filter(({ isVisible }) => !!isVisible);

  return (
    <>
      <Stack direction="row" spacing={1} data-a="social-buttons">
        {video
          ? socialButtons.map(({ provider, Icon, disabled, tooltip, onClick, adOpsDataAIcon }) => (
              <Tooltip key={provider} title={tooltip} placement="bottom">
                <div>
                  <IconButton size="medium" disabled={disabled} onClick={disabled ? null : onClick}>
                    <Icon data-a={adOpsDataAIcon} />
                  </IconButton>
                </div>
              </Tooltip>
            ))
          : socialButtons.map(({ provider }) => (
              <Skeleton key={provider} variant="circular" className={styles.ShareSkeleton} />
            ))}
      </Stack>

      {showShareDialog && canUseInternalShare && (
        <DefaultSuspense>
          <InternalShare
            video={video}
            channelId={channelId}
            onClose={() => setShowShareDialog(false)}
            fromInlineChannel={fromInlineChannel}
          />
        </DefaultSuspense>
      )}
      {showShareDialog && canUseExternalShare && (
        <DefaultSuspense>
          <ExternalShare
            video={video}
            channelId={channelId}
            routePath={props.routePath}
            onClose={() => setShowShareDialog(false)}
            fromInlineChannel={fromInlineChannel}
          />
        </DefaultSuspense>
      )}
    </>
  );
}

SocialButtons.propTypes = {
  video: videoPropTypes,
  channelId: PropTypes.number,
  channelType: PropTypes.string,
  routePath: PropTypes.oneOf([
    DEFAULT_VIEW_PATH,
    SEARCH_VIEW_PATH,
    SHARE_VIEW_PATH,
    SINGLE_VIEW_PATH,
    SEARCH_AI_VIEW_PATH,
  ]).isRequired,
  fromInlineChannel: PropTypes.bool,
};

export default SocialButtons;
